import React from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { isAdminUser } from '../../utils/adminPermissions';

interface AdminRouteProps {
  children: React.ReactElement;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const raw = user as Record<string, unknown> | null;
  const innerUser =
    raw && typeof raw === 'object' && 'user' in raw && raw.user && typeof raw.user === 'object'
      ? (raw.user as Record<string, unknown>)
      : raw;

  const accountStatus: string | undefined =
    innerUser && typeof innerUser.accountStatus === 'string'
      ? (innerUser.accountStatus as string)
      : undefined;

  if (accountStatus && accountStatus !== 'good') {
    const isSuspended = accountStatus === 'suspended';

    return (
      <main className="mx-auto max-w-2xl px-4 py-10">
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-5 text-sm text-amber-900 shadow-sm">
          <h1 className="text-base font-semibold text-amber-900">
            {isSuspended ? 'Account suspended' : 'Account needs attention'}
          </h1>
          <p className="mt-2 text-xs text-amber-800">
            {isSuspended
              ? 'Your TELLURVISION admin access has been suspended. Please contact support to review your account and restore access.'
              : 'Your TELLURVISION account is past due. Please update your billing details to regain full access to the admin area.'}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {!isSuspended && (
              <Link
                to="/payment"
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              >
                Go to Payments
              </Link>
            )}
            <Link
              to="/"
              className="inline-flex items-center rounded-md border border-amber-300 bg-amber-100 px-3 py-1.5 text-xs font-medium text-amber-900 shadow-sm hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
            >
              Return home
            </Link>
          </div>
        </div>
      </main>
    );
  }

  if (!isAdminUser(user)) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-10">
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-4 py-5 text-sm text-slate-900 dark:text-slate-100 shadow-sm">
          <h1 className="text-base font-semibold text-slate-900 dark:text-slate-100">Admin access required</h1>
          <p className="mt-2 text-xs text-slate-700 dark:text-slate-300">
            This panel is only available to TELLURVISION administrators.
          </p>
        </div>
      </main>
    );
  }

  return children;
};

export default AdminRoute;
